// Sales Expenses › Add Expense: one shipping / insurance / duty… cost added
// to a sale that's already saved, without opening the sale. Same save path
// as the sale form's "Additional Charges & Expenses" (utils/saleExpenses):
// the saleExpenses doc, bank row + balance when paid now, the books entry.
// "Charge customer" also raises the sale's total (earliest due date in
// separate mode) so Receivable picks it up, exactly as the sale form does.

import { useEffect, useMemo, useState } from 'react';
import { collection, doc, getDocs, runTransaction, serverTimestamp } from 'firebase/firestore';
import { getClientDb } from '../firebase/clientApp';
import { useAuth } from '../context/AuthContext';
import { useSettings } from '../hooks/useSettings';
import { fmtMoney } from '../utils/currency';
import { shortInvoiceNo } from '../utils/invoiceNo';
import { logActivity } from '../utils/activity';
import { aggregateFromDueGroups, dueGroupStatus, dueGroupOutstanding } from '../utils/dueGroups';
import {
  SALE_EXPENSE_CATEGORIES, newSaleExpenseRow, planSaleExpenses, extraCostsOf,
  readSaleExpenseAccounts, applySaleExpenses,
} from '../utils/saleExpenses';
import { Button, Modal, ModalBanner, FormField, EntityCombobox } from './ui';
import type { BankAccount, DueGroup, Entity, PaymentStatus, SaleInvoice } from '../types';

function todayISO() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
const round2 = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100;

export default function AddSaleExpenseModal({ entities, accounts, onClose }: {
  entities: Entity[]; accounts: BankAccount[]; onClose: () => void;
}) {
  const { user, business } = useAuth();
  const settings = useSettings();
  const money = (v: number) => fmtMoney(v ?? 0, settings.currency);
  const entityName = (id?: string) => (id ? entities.find(e => e.id === id)?.companyName ?? '—' : '—');

  const [sales, setSales] = useState<SaleInvoice[]>([]);
  const [saleId, setSaleId] = useState('');
  const [category, setCategory] = useState(SALE_EXPENSE_CATEGORIES[0]);
  const [description, setDescription] = useState('');
  const [amount, setAmount] = useState('');
  const [date, setDate] = useState(todayISO());
  const [vendorEntityId, setVendorEntityId] = useState('');
  const [chargeToCustomer, setChargeToCustomer] = useState(false);
  const [payNow, setPayNow] = useState(false);
  const [bankAccountId, setBankAccountId] = useState((accounts.find(a => a.isDefault) ?? accounts[0])?.id ?? '');
  const [dueDate, setDueDate] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const db = getClientDb();
    if (!db || !business) return;
    getDocs(collection(db, 'sales'))
      .then(snap => setSales(snap.docs.map(d => ({ id: d.id, ...d.data() } as SaleInvoice))
        .sort((a, b) => (b.saleDate ?? '').localeCompare(a.saleDate ?? ''))))
      .catch(() => setSales([]));
  }, [business]);

  // "SL-0042 · Customer · 12 Oct 2026 · $9,000.00", newest first.
  const saleOptions = useMemo(() => sales.map(s => ({
    id: s.id,
    companyName: `${shortInvoiceNo(s.invoiceNo)} · ${entityName(s.toEntityId)} · ${s.saleDate ? new Date(s.saleDate + 'T00:00:00').toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : ''} · ${money(s.totalAmountUSD)}`,
  // eslint-disable-next-line react-hooks/exhaustive-deps
  })), [sales, entities]);
  const sale = sales.find(s => s.id === saleId) ?? null;
  const acct = accounts.find(a => a.id === bankAccountId) ?? null;

  async function save() {
    const amt = round2(parseFloat(amount));
    if (!sale) return setError('Pick the sale this cost belongs to.');
    if (!description.trim()) return setError('Enter a short description.');
    if (isNaN(amt) || amt <= 0) return setError('Enter a valid amount.');
    if (payNow && !bankAccountId) return setError('Choose the account you paid from.');

    setSaving(true); setError('');
    const db = getClientDb();
    if (!db || !business || !user) { setError('Not connected.'); setSaving(false); return; }

    const row = {
      ...newSaleExpenseRow(date),
      category, description: description.trim(), amount: String(amt), date,
      vendorEntityId, chargeToCustomer, payNow, bankAccountId: payNow ? bankAccountId : '', dueDate: payNow ? '' : dueDate,
    };
    const plan = planSaleExpenses(db, [row], []);
    const saleRef = doc(db, 'sales', sale.id);
    const counterRef = doc(db, 'counters', business.id);

    try {
      await runTransaction(db, async tx => {
        // ── Reads ──
        const cs = await tx.get(counterRef);
        const ss = await tx.get(saleRef);
        if (!ss.exists()) throw new Error('That sale no longer exists.');
        const accts = await readSaleExpenseAccounts(tx, plan);
        const cur = { id: ss.id, ...ss.data() } as SaleInvoice;
        const counterStart = cs.exists() ? (cs.data().saleExpense ?? 0) : 0;

        // ── Writes ──
        tx.set(counterRef, { saleExpense: counterStart + 1 }, { merge: true });
        applySaleExpenses(tx, db, plan, accts, {
          businessId: business.id, userId: user.id, baseCurrency: settings.currency,
          prefix: settings.saleExpensePrefix || 'SE', counterStart,
          saleId: sale.id, saleInvoiceNo: cur.invoiceNo, saleDate: cur.saleDate, customerEntityId: cur.toEntityId,
        });

        // The sale's own figures, same fields the sale form writes.
        const update: Record<string, unknown> = {
          extraCosts: [...(cur.extraCosts ?? []), ...extraCostsOf(plan)],
          updatedAt: serverTimestamp(),
        };
        if (chargeToCustomer) {
          const newTotal = round2((cur.totalAmountUSD ?? 0) + amt);
          update.totalAmountUSD = newTotal;
          update.chargesUSD = round2((cur.chargesUSD ?? 0) + amt);
          const groups = cur.dueDateMode === 'separate' && cur.dueGroups?.length ? cur.dueGroups.map(g => ({ ...g })) as DueGroup[] : null;
          if (groups) {
            // Charges are payable with the earliest due date still open (else the first).
            const gi = Math.max(0, groups.findIndex(g => dueGroupOutstanding(g) > 0.005));
            groups[gi].amountUSD = round2(groups[gi].amountUSD + amt);
            groups[gi].paymentStatus = dueGroupStatus(groups[gi]);
            const agg = aggregateFromDueGroups(groups);
            update.dueGroups = groups;
            update.paymentStatus = agg.paymentStatus;
            update.dueDate = agg.dueDate;
          } else {
            const settled = (cur.paidUSD ?? 0) + (cur.creditedUSD ?? 0);
            const status: PaymentStatus = settled >= newTotal - 0.005 ? 'paid' : settled > 0 ? 'partial' : 'unpaid';
            update.paymentStatus = status;
          }
        } else {
          update.sellingExpensesUSD = round2((cur.sellingExpensesUSD ?? 0) + amt);
        }
        tx.update(saleRef, update);

        logActivity(tx, db, business.id, user, {
          module: 'sale', action: 'edit', recordId: sale.id, recordNo: cur.invoiceNo, partyId: cur.toEntityId,
          amountUSD: amt,
          note: `Expense added: ${description.trim()} (${chargeToCustomer ? 'charged to customer' : 'our cost'})`,
        });
      });
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save the expense.');
    }
    setSaving(false);
  }

  const seg = (on: boolean): React.CSSProperties => ({
    flex: 1, height: 34, borderRadius: 'var(--r-sm)', fontSize: 12.5, fontWeight: 600, cursor: 'pointer',
    border: on ? '1.5px solid var(--primary)' : '1.5px solid var(--border)',
    background: on ? 'var(--primary-light)' : 'var(--surface)', color: on ? 'var(--primary)' : 'var(--text-2)',
  });

  return (
    <Modal open onClose={onClose} hideHead size="lg"
      footer={<>
        <Button variant="secondary" onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="primary" onClick={save} loading={saving}>Add</Button>
      </>}>
      <ModalBanner title={description.trim() || category} eyebrow="Add Sales Expense" onClose={onClose} />

      {error && <div className="msg msg-error" style={{ marginBottom: 12 }}>{error}</div>}

      <FormField label="Sale" required>
        <EntityCombobox entities={saleOptions} value={saleId} onChange={setSaleId}
          placeholder="Type an invoice no or customer…" />
      </FormField>
      {sale && (
        <div style={{ fontSize: 12, color: 'var(--text-3)', margin: '-6px 0 12px' }}>
          {entityName(sale.toEntityId)} · total {money(sale.totalAmountUSD)}
          {(sale.extraCosts?.length ?? 0) > 0 && ` · ${sale.extraCosts!.length} expense${sale.extraCosts!.length === 1 ? '' : 's'} already on it`}
        </div>
      )}

      <div className="form-row-2">
        <FormField label="Category" required>
          <select value={category} onChange={e => setCategory(e.target.value)}>
            {SALE_EXPENSE_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </FormField>
        <FormField label="Date" required>
          <input type="date" value={date} max={todayISO()} onChange={e => setDate(e.target.value)} />
        </FormField>
      </div>
      <FormField label="Description" required>
        <input value={description} onChange={e => setDescription(e.target.value)} placeholder="e.g. FedEx to Dubai" />
      </FormField>
      <div className="form-row-2">
        <FormField label={`Amount (${settings.currency})`} required>
          <input type="number" step="0.01" min="0.01" className="no-spinner" value={amount} onChange={e => setAmount(e.target.value)} />
        </FormField>
        <FormField label="Paid to (optional)">
          <EntityCombobox entities={entities.map(e => ({ id: e.id, companyName: e.companyName }))}
            value={vendorEntityId} onChange={setVendorEntityId} clearLabel="No vendor" />
        </FormField>
      </div>

      <FormField label="Who pays?" required>
        <div style={{ display: 'flex', gap: 8 }}>
          <button type="button" style={seg(!chargeToCustomer)} onClick={() => setChargeToCustomer(false)}
            title="Our own selling cost: lowers profit, not on the invoice">Our cost</button>
          <button type="button" style={seg(chargeToCustomer)} onClick={() => setChargeToCustomer(true)}
            title="Added to the sale's total, so the customer pays it">Charge customer</button>
        </div>
      </FormField>
      {chargeToCustomer && sale && (
        <div style={{ fontSize: 12, color: 'var(--text-3)', margin: '-6px 0 12px' }}>
          The sale's total goes from {money(sale.totalAmountUSD)} to {money(round2(sale.totalAmountUSD + (parseFloat(amount) || 0)))}.
        </div>
      )}

      <div style={{ marginTop: 6, padding: '12px 14px', border: '1px solid var(--border)', borderRadius: 'var(--r-sm)', background: 'var(--surface-2)' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
          <input type="checkbox" checked={payNow} onChange={e => setPayNow(e.target.checked)} />
          I paid this now
        </label>
        <div style={{ marginTop: 10 }}>
          {payNow ? (
            <FormField label="Paid from" required>
              <select value={bankAccountId} onChange={e => setBankAccountId(e.target.value)}>
                <option value="">Select account…</option>
                {accounts.map(a => <option key={a.id} value={a.id}>{a.name} · {fmtMoney(a.currentBalance, a.currency)}</option>)}
              </select>
            </FormField>
          ) : (
            <FormField label="Due date (optional)">
              <input type="date" value={dueDate} onChange={e => setDueDate(e.target.value)} />
            </FormField>
          )}
          {payNow && acct && acct.currency !== settings.currency && (
            <div style={{ fontSize: 11.5, color: 'var(--text-3)', marginTop: -6 }}>Paid from a {acct.currency} account.</div>
          )}
        </div>
      </div>
    </Modal>
  );
}
